<script lang="ts">
	import { get } from 'svelte/store';
	import { createEventDispatcher } from 'svelte';
	//Formating
	import { format } from 'svelte-i18n';
	//Design system components
	import { Text, IconButton, Container, Popover, Icon } from '@Shelly/shelly-design-system';
	//Helpers
	import { devices } from 'src/stores/lists/devices';
	import { doesSupportLocalActions } from 'src/helpers/actions/local/supported/local_action_supported_helpers';
	import { doesSupportVirtualLocalActions } from 'src/helpers/actions/local/core/local_action_virtual_helpers';
	import { getURLActions } from 'src/helpers/actions/webhooks/core/webhook_url_action_helpers';
	//Components
	import DeviceActionToUrl from './DeviceActions/DeviceActionToURL.svelte';
	import LocalUrlActions from './LocalURLActions.svelte';
	import URLModalActions from './URLModalActions.svelte';
	//Types
	import type { Device } from 'src/types/Device';
	import {
		type DevicesUrls,
		type StaticUrls,
	} from 'src/helpers/actions/webhooks/core/webhook_url_action_helpers';
	import type { WebhookURLError } from 'src/helpers/actions/webhooks/core/webhook_url_action_helpers';

	export let urls: string[],
		error: WebhookURLError,
		tokens: string[],
		device: Device,
		allDevices: Device[] = null;

	let staticUrls: StaticUrls = [];
	let devicesUrls: DevicesUrls = [];
	let showEditUrlModal = false;
	let editUrlIndex = -1;
	let editUrlValue = '';

	const hasLocalActions = doesSupportLocalActions(device) || doesSupportVirtualLocalActions(device);
	const dispatcher = createEventDispatcher();

	const onDelete = (index: number) => {
		if (urls.length > 1) {
			urls.splice(index, 1);
			urls = urls;
		} else {
			urls = [];
		}
	};

	const openEditUrlModal = (index: number) => {
		editUrlIndex = index;
		editUrlValue = urls[index];
		showEditUrlModal = true;
	};

	$: if (urls) {
		const urlActions = getURLActions(urls, device);
		staticUrls = urlActions.staticUrls;
		devicesUrls = urlActions.devicesUrls;
	}
</script>

<div class="d-flex align-items-center mb-s">
	<Text styleType="h2">
		{$format('url_actions', { default: 'URL Actions' })}
	</Text>

	<Popover type="hover" hideCloseBtn placement="top">
		<IconButton
			aria-label={$format('button_info', { default: 'Button info' })}
			class="ml-xs"
			icon="fas fa-circle-info"
			variant="help"
		/>
		<svelte:fragment slot="customContent">
			<Text styleType="body-small">
				{$format('url_actions_description', {
					default:
						'Use URL Actions to integrate with web services or interact with other devices. Configure URLs to trigger automatically when an action is executed, or link actions from another device for seamless interaction.',
				})}
			</Text>
		</svelte:fragment>
	</Popover>
</div>

<Container type="borderDashed" class="mb-m">
	{#each staticUrls as staticUrl}
		{@const i = staticUrl.index}
		{@const url = urls[i]}
		{#if !/http:\/\/(127.0.0.1|localhost)\/rpc\//.test(url)}
			<Container type="primary-10" padding="xs" class="mb-s">
				<div class="d-flex align-items-center justify-content-between">
					<Container padding="xs" noScroll class="width-full mr-s">
						<div class="d-flex align-items-center justify-content-between">
							<div class="d-flex align-items-center">
								<Icon class="mr-s" size="s" icon="fas fa-link" />
								<Text class="mr-s" styleType="controls-small">
									{urls[i]}
								</Text>
							</div>
							<div class="d-flex align-items-center">
								<IconButton
									aria-label={$format('button_edit_url', {
										default: 'Button edit url',
									})}
									icon="fas fa-pen"
									on:click={() => {
										openEditUrlModal(i);
									}}
								/>
							</div>
						</div>
					</Container>
					<IconButton
						aria-label={$format('button_delete_url', {
							default: 'Button delete url',
						})}
						icon="fas fa-trash"
						variant="danger"
						on:click={() => {
							onDelete(i);
						}}
					/>
				</div>
			</Container>
		{/if}
	{/each}

	<div class="text-center">
		<IconButton
			class="max-width-full"
			icon="fas fa-plus"
			variant="secondary"
			disabled={urls.length > 4}
			on:click={() => {
				openEditUrlModal(urls.length);
			}}
		>
			{$format('button_add_url', { default: 'Add URL' })}
		</IconButton>
	</div>

	<Text styleType="body-small" class="mb-s mt-s text-center">
		{$format('or', {
			default: 'or',
		})}
	</Text>

	{#each devicesUrls as deviceUrl}
		{@const i = deviceUrl.index}
		{@const url = urls[i]}
		{#if !/http:\/\/(127.0.0.1|localhost)\/rpc\//.test(url)}
			<Container type="primary-10" padding="xs" class="mb-s">
				<DeviceActionToUrl
					foundDevices={deviceUrl.foundDevices}
					devicesWithSameIP={deviceUrl.devicesWithSameIP}
					on:edit={(e) => {
						dispatcher('edit', {
							urlIndex: i,
							deviceAction: e.detail,
						});
					}}
				>
					<IconButton
						aria-label={$format('button_delete_url', {
							default: 'Button delete url',
						})}
						slot="delete-btn"
						icon="fas fa-trash"
						variant="danger"
						on:click={() => {
							onDelete(i);
						}}
					/>
				</DeviceActionToUrl>
				<Container padding="xs" noScroll>
					<div class="d-flex align-items-center justify-content-between">
						<div class="d-flex align-items-center">
							<Icon class="mr-s" size="s" icon="fas fa-link" />
							<Text class="mr-s" styleType="controls-small">
								{urls[i]}
							</Text>
						</div>
						<IconButton
							aria-label={$format('button_edit_url', {
								default: 'Button edit url',
							})}
							icon="fas fa-pen"
							on:click={() => {
								openEditUrlModal(i);
							}}
						/>
					</div>
				</Container>
			</Container>
		{/if}
	{/each}

	<div class="text-center">
		<IconButton
			class="max-width-full"
			icon="fas fa-plus"
			variant="secondary"
			disabled={urls.length > 4}
			on:click={() => {
				allDevices = Object.values(get(devices)).filter(
					(dev) =>
						!dev.shared &&
						dev.type != 'bthome-device' &&
						(doesSupportLocalActions(dev) || doesSupportVirtualLocalActions(dev)),
				);
			}}
		>
			{$format('button_add_url_device_action', {
				default: 'Add action from my devices',
			})}
		</IconButton>
	</div>
</Container>
{#if hasLocalActions}
	<LocalUrlActions {device} bind:urls on:action />
{/if}

<URLModalActions
	{tokens}
	{error}
	bind:editUrlValue
	bind:showModal={showEditUrlModal}
	on:edit={(e) => {
		urls[editUrlIndex] = e.detail;
	}}
/>
